/**
 * FACTURACION PAGE - Listado de facturas SAR/CAI con tabla server-side
 * CRUD: crear factura desde orden, ver detalle, registrar pago, anular.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Receipt,
  Plus,
  MoreHorizontal,
  Eye,
  CreditCard,
  Ban,
  AlertCircle,
  RefreshCw,
  Search,
  X,
  Printer,
} from "lucide-react";
import { ResponsiveTable, useIsMobile } from "@/components/ui/responsive-table";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
} from "@/components/ui/data-table";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DateRangeFilter } from "@/components/ui/date-range-filter";

import { useApiError } from "@/hooks/use-api-error";
import { useServerDataTable } from "@/hooks/use-server-data-table";
import { useAnularFactura, useFactura } from "@/hooks/use-facturacion";
import { useSucursal, ALL_SUCURSALES_ID } from "@/contexts/sucursal-context";
import { useSucursales } from "@/hooks/use-sucursales";
import {
  getFacturas,
  ESTADOS_FACTURA,
  type FacturaListItem,
  type EstadoFactura,
  type Factura,
} from "@/hooks/use-facturacion";

import { FacturaFormDialog } from "./components/factura-form-dialog";
import { FacturaDetailSheet } from "./components/factura-detail-sheet";
import { PagoFormDialog } from "./components/pago-form-dialog";
import { PostFacturaDialog } from "./components/post-factura-dialog";
import { TicketFactura } from "./components/ticket-factura";
import { useImprimirFacturaTermica } from "./components/use-imprimir-factura-termica";

// =============================================================================
// Estado badge
// =============================================================================

const ESTADO_VARIANT: Record<EstadoFactura, "default" | "secondary" | "destructive" | "outline"> = {
  BORRADOR: "outline",
  EMITIDA: "secondary",
  PAGADA: "default",
  ANULADA: "destructive",
};

// =============================================================================
// Page
// =============================================================================

export default function FacturacionPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isMobile = useIsMobile();
  const { isAllSucursalesSelected, sucursalActiva, canViewAllSucursales } = useSucursal();
  const { data: sucursales = [] } = useSucursales();

  // Determinar filtro inicial de sucursal basado en contexto
  const getInitialSucursalFilter = useCallback(() => {
    if (canViewAllSucursales && isAllSucursalesSelected) {
      return "all";
    }
    if (sucursalActiva && sucursalActiva.id !== ALL_SUCURSALES_ID) {
      return String(sucursalActiva.id);
    }
    return "all";
  }, [canViewAllSucursales, isAllSucursalesSelected, sucursalActiva]);

  // Dialog state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isPagoOpen, setIsPagoOpen] = useState(false);
  const [isAnularOpen, setIsAnularOpen] = useState(false);
  const [selectedFacturaId, setSelectedFacturaId] = useState<number | null>(null);
  const [selectedForPago, setSelectedForPago] = useState<FacturaListItem | null>(null);
  const [selectedForAnular, setSelectedForAnular] = useState<FacturaListItem | null>(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState("");
  const [postFactura, setPostFactura] = useState<Factura | null>(null);

  // Mutations
  const anularMutation = useAnularFactura({
    onSuccess: () => {
      toast.warning(t("facturacion.toast.anulada"));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  // Ticket térmico
  const ticketRef = useRef<HTMLDivElement>(null);
  const imprimirTermica = useImprimirFacturaTermica(ticketRef);
  const [printPending, setPrintPending] = useState(false);
  const { data: facturaParaTicket } = useFactura(selectedFacturaId ?? 0, {
    enabled: selectedFacturaId !== null,
  });

  // Disparar impresión cuando la factura esté cargada y haya una impresión pendiente
  useEffect(() => {
    if (printPending && facturaParaTicket) {
      setPrintPending(false);
      setTimeout(() => imprimirTermica(), 100);
    }
  }, [printPending, facturaParaTicket, imprimirTermica]);

  // Handlers
  const handleViewDetail = useCallback((item: FacturaListItem) => {
    setSelectedFacturaId(item.id);
    setIsDetailOpen(true);
  }, []);

  const handleRegistrarPago = useCallback((item: FacturaListItem) => {
    setSelectedForPago(item);
    setIsPagoOpen(true);
  }, []);

  const handleAnular = useCallback((item: FacturaListItem) => {
    setSelectedForAnular(item);
    setMotivoAnulacion("");
    setIsAnularOpen(true);
  }, []);

  const handleConfirmAnular = () => {
    if (!selectedForAnular) return;
    anularMutation.mutate({ id: selectedForAnular.id, motivo: motivoAnulacion });
    setIsAnularOpen(false);
    setSelectedForAnular(null);
  };

  const handleFormSuccess = (factura: Factura) => {
    setIsFormOpen(false);
    toast.success(t("facturacion.toast.created"));
    refetch();
    setPostFactura(factura);
  };

  const handleImprimirRecibo = useCallback((item: FacturaListItem) => {
    setSelectedFacturaId(item.id);
    setPrintPending(true);
  }, []);

  const handlePagoSuccess = (result?: import("@/hooks/use-facturacion").PagoResult) => {
    setIsPagoOpen(false);
    const facturaEsPaciente = selectedForPago && selectedForPago.cliente === null;
    setSelectedForPago(null);
    const vuelto = result ? parseFloat(result.vuelto) : 0;
    if (vuelto > 0) {
      toast.success(t("facturacion.pago.toastVuelto", { vuelto: vuelto.toFixed(2) }));
    } else {
      toast.success(t("facturacion.toast.pagoRegistrado"));
    }
    refetch();
    // Auto-imprimir recibo si es factura de paciente (sin cliente)
    if (facturaEsPaciente && selectedFacturaId !== null) {
      setPrintPending(true);
    }
  }; // Columns
  const columns: ColumnDef<FacturaListItem>[] = useMemo(
    () => [
      {
        accessorKey: "numero",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("facturacion.columns.numero")} />
        ),
        cell: ({ row }) => (
          <button
            className="font-mono text-sm text-primary hover:underline text-left"
            onClick={() => handleViewDetail(row.original)}
          >
            {row.original.numero}
          </button>
        ),
      },
      {
        accessorKey: "fecha",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("facturacion.columns.fecha")} />
        ),
        cell: ({ row }) => <span className="text-sm">{row.original.fecha}</span>,
      },
      {
        accessorKey: "ordenes_count",
        header: () => t("facturacion.columns.ordenes"),
        cell: ({ row }) => (
          <span className="text-sm text-center block">{row.original.ordenes_count}</span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "nombre_cliente",
        header: () => t("facturacion.columns.cliente"),
        cell: ({ row }) => (
          <div className="text-sm">
            <p className="font-medium">
              {row.original.nombre_cliente || row.original.cliente_nombre || "-"}
            </p>
            {row.original.rtn_cliente && (
              <p className="text-xs text-muted-foreground">{row.original.rtn_cliente}</p>
            )}
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "paciente_nombre",
        header: () => t("facturacion.columns.paciente"),
        cell: ({ row }) => <span className="text-sm">{row.original.paciente_nombre}</span>,
        enableSorting: false,
      },
      ...(isAllSucursalesSelected
        ? [
            {
              accessorKey: "sucursal_nombre" as const,
              header: () => t("facturacion.columns.sucursal"),
              cell: ({ row }: { row: { original: FacturaListItem } }) => (
                <span className="text-sm text-muted-foreground">
                  {row.original.sucursal_nombre}
                </span>
              ),
              enableSorting: false,
            },
          ]
        : []),
      {
        accessorKey: "total",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("facturacion.columns.total")} />
        ),
        cell: ({ row }) => {
          const descuento = parseFloat(row.original.descuento_pagos_directos || "0");
          return (
            <div className="flex flex-col">
              <span className="font-mono text-sm font-medium">L.{row.original.total}</span>
              {descuento > 0 && (
                <span className="text-xs text-green-600 dark:text-green-400">
                  −L.{descuento.toFixed(2)} desc.
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "estado",
        header: () => t("facturacion.columns.estado"),
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="flex flex-col gap-1">
              <Badge variant={ESTADO_VARIANT[item.estado]}>
                {t(`facturacion.estados.${item.estado}`)}
              </Badge>
              {item.esta_pagada && item.estado !== "PAGADA" && (
                <Badge className="bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30 text-xs">
                  {t("facturacion.columns.pagada")}
                </Badge>
              )}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: "actions",
        header: () => t("common.actions"),
        cell: ({ row }) => {
          const item = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleViewDetail(item)}>
                  <Eye className="mr-2 h-4 w-4" />
                  {t("common.viewDetail")}
                </DropdownMenuItem>
                {item.esta_pagada && item.estado !== "BORRADOR" && item.estado !== "ANULADA" && (
                  <DropdownMenuItem onClick={() => handleImprimirRecibo(item)}>
                    <Printer className="mr-2 h-4 w-4" />
                    {t("facturacion.imprimirFactura")}
                  </DropdownMenuItem>
                )}
                {item.estado !== "ANULADA" && item.estado !== "PAGADA" && (
                  <DropdownMenuItem onClick={() => handleRegistrarPago(item)}>
                    <CreditCard className="mr-2 h-4 w-4" />
                    {t("facturacion.pago.submit")}
                  </DropdownMenuItem>
                )}
                {item.estado !== "ANULADA" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleAnular(item)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Ban className="mr-2 h-4 w-4" />
                      {t("facturacion.confirmAnular")}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        enableSorting: false,
      },
    ],
    [
      t,
      handleViewDetail,
      handleRegistrarPago,
      handleAnular,
      handleImprimirRecibo,
      isAllSucursalesSelected,
    ]
  );

  // Server-side data table
  const {
    table,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable({
    columns,
    fetchFn: getFacturas,
    queryKey: "facturas",
    storageKey: "facturas_page_size",
    defaultPageSize: 20,
    filters: [
      { key: "estado", defaultValue: "all", urlParam: "estado" },
      { key: "sucursal", defaultValue: "all" },
      { key: "fecha_desde", defaultValue: "" },
      { key: "fecha_hasta", defaultValue: "" },
    ],
    initialFilters: {
      sucursal: getInitialSucursalFilter(),
    },
  });

  // Sincronizar filtro de sucursal cuando cambia el contexto
  useEffect(() => {
    if (!isAllSucursalesSelected && sucursalActiva && sucursalActiva.id !== ALL_SUCURSALES_ID) {
      const sucursalId = String(sucursalActiva.id);
      if (filters.sucursal !== sucursalId) {
        setFilter("sucursal", sucursalId);
      }
    }
  }, [sucursalActiva, isAllSucursalesSelected, filters.sucursal, setFilter]);

  const columnLabels = {
    numero: t("facturacion.columns.numero"),
    fecha: t("facturacion.columns.fecha"),
    ordenes_count: t("facturacion.columns.ordenes"),
    total: t("facturacion.columns.total"),
  };

  // Mobile card
  const renderMobileCard = useCallback(
    (item: FacturaListItem) => (
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={ESTADO_VARIANT[item.estado]} className="text-xs">
              {t(`facturacion.estados.${item.estado}`)}
            </Badge>
            {item.esta_pagada && item.estado !== "PAGADA" && (
              <Badge className="bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30 text-xs">
                {t("facturacion.columns.pagada")}
              </Badge>
            )}
          </div>
          <button
            className="font-mono text-sm text-primary hover:underline mt-1 block"
            onClick={() => handleViewDetail(item)}
          >
            {item.numero}
          </button>
          <p className="text-sm font-medium mt-0.5">{item.nombre_cliente}</p>
          <p className="text-xs text-muted-foreground">
            {item.paciente_nombre} &middot; {item.fecha}
          </p>
          {isAllSucursalesSelected && item.sucursal_nombre && (
            <p className="text-xs text-muted-foreground">{item.sucursal_nombre}</p>
          )}
          <div className="mt-1">
            <p className="font-mono text-sm font-semibold">L.{item.total}</p>
            {parseFloat(item.descuento_pagos_directos || "0") > 0 && (
              <p className="text-xs text-green-600 dark:text-green-400">
                −L.{parseFloat(item.descuento_pagos_directos).toFixed(2)} desc.
              </p>
            )}
          </div>
          <div className="flex gap-1 mt-3">
            <Button variant="outline" size="sm" onClick={() => handleViewDetail(item)}>
              <Eye className="h-3 w-3 mr-1" />
              {t("common.viewDetail")}
            </Button>
            {item.estado !== "ANULADA" && item.estado !== "PAGADA" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleRegistrarPago(item)}>
                    <CreditCard className="mr-2 h-4 w-4" />
                    {t("facturacion.pago.submit")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => handleAnular(item)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Ban className="mr-2 h-4 w-4" />
                    {t("facturacion.confirmAnular")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    ),
    [t, handleViewDetail, handleRegistrarPago, handleAnular, isAllSucursalesSelected]
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Receipt className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{t("facturacion.title")}</h1>
            <p className="text-sm text-muted-foreground hidden sm:block">
              {t("facturacion.description")}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
          <Button
            size={isMobile ? "sm" : "default"}
            onClick={() => setIsFormOpen(true)}
            disabled={isAllSucursalesSelected}
            title={isAllSucursalesSelected ? t("facturacion.selectSucursalToCreate") : undefined}
          >
            <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {isMobile ? t("common.new") : t("facturacion.newFactura")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("facturacion.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Sucursal */}
              {isAllSucursalesSelected ? (
                <Select value={filters.sucursal} onValueChange={(v) => setFilter("sucursal", v)}>
                  <SelectTrigger className="w-[calc(50%-4px)] sm:w-[160px]">
                    <SelectValue placeholder={t("facturacion.columns.sucursal")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("sucursal.allBranches")}</SelectItem>
                    {sucursales.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="hidden sm:flex items-center h-9 px-3 border rounded-md bg-muted/50 text-sm">
                  <span className="text-muted-foreground mr-1.5">
                    {t("facturacion.columns.sucursal")}:
                  </span>
                  <span className="font-medium truncate max-w-[120px]">
                    {sucursalActiva?.nombre || "-"}
                  </span>
                </div>
              )}

              {/* Estado */}
              <Select value={filters.estado} onValueChange={(value) => setFilter("estado", value)}>
                <SelectTrigger className="w-[calc(50%-4px)] sm:w-[160px]">
                  <SelectValue placeholder={t("facturacion.filterByEstado")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("facturacion.allEstados")}</SelectItem>
                  {ESTADOS_FACTURA.map((e) => (
                    <SelectItem key={e.value} value={e.value}>
                      {t(`facturacion.estados.${e.value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Fecha */}
              <DateRangeFilter
                desde={filters.fecha_desde}
                hasta={filters.fecha_hasta}
                onChange={(desde, hasta) => {
                  setFilter("fecha_desde", desde);
                  setFilter("fecha_hasta", hasta);
                }}
                className="w-full sm:w-[200px]"
              />

              {hasActiveFilters && (
                <Button variant="secondary" size="icon" onClick={clearFilters} className="shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardDescription>
              {t("facturacion.showingResults", { count: totalCount })}
            </CardDescription>
            {!isMobile && <DataTableViewOptions table={table} columnLabels={columnLabels} />}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyMessage={t("facturacion.noResults")}
            hideOnTablet={["paciente_nombre"]}
            renderMobileCard={renderMobileCard}
          />
          <DataTablePagination table={table} totalRows={totalCount} />
        </CardContent>
      </Card>

      {/* Ticket térmico oculto para impresión */}
      {facturaParaTicket && (
        <div className="hidden">
          <TicketFactura ref={ticketRef} factura={facturaParaTicket} />
        </div>
      )}

      {/* Dialogs */}
      <FacturaFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSuccess={handleFormSuccess}
      />

      <PostFacturaDialog
        factura={postFactura}
        onClose={() => setPostFactura(null)}
        onPrint={(facturaId) => {
          setSelectedFacturaId(facturaId);
          setPrintPending(true);
        }}
      />

      <FacturaDetailSheet
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        facturaId={selectedFacturaId}
      />

      <PagoFormDialog
        open={isPagoOpen}
        onOpenChange={(open) => {
          setIsPagoOpen(open);
          if (!open) setSelectedForPago(null);
        }}
        factura={selectedForPago}
        onSuccess={handlePagoSuccess}
      />

      <ConfirmDialog
        open={isAnularOpen}
        onOpenChange={setIsAnularOpen}
        title={t("facturacion.confirmAnular")}
        description={
          <div className="space-y-3">
            <p>
              {t("facturacion.confirmAnularDesc", {
                numero: selectedForAnular?.numero || "",
              })}
            </p>
            <Input
              placeholder={t("facturacion.motivoPlaceholder")}
              value={motivoAnulacion}
              onChange={(e) => setMotivoAnulacion(e.target.value)}
            />
          </div>
        }
        onConfirm={handleConfirmAnular}
        variant="destructive"
        isLoading={anularMutation.isPending}
      />
    </div>
  );
}
